import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Copy, Check, FileText, Shield, AlertTriangle } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "@/hooks/use-toast";

interface ResponseScript {
  label: string;
  tag: string;
  script: string;
  why: string;
}

const RESPONSE_SCRIPTS: Record<string, ResponseScript[]> = {
  GASLIGHTING: [
    {
      label: "A",
      tag: "The Hard Grey Rock",
      script: "I hear you.",
      why: "This denies the manipulator the emotional reaction they are seeking. By refusing to engage with their distorted version of events, you remove their leverage.",
    },
    {
      label: "B",
      tag: "The Boundary Setter",
      script: "I'm not willing to argue about what happened. I know what I experienced.",
      why: "This asserts your reality without attacking theirs. It removes the debate they're trying to create and re-centers your own perception.",
    },
    {
      label: "C",
      tag: "The Exit",
      script: "I'm going to step away from this conversation now.",
      why: "Disengagement is the most powerful tool against gaslighting. It signals that their distortion tactic has failed and you will not participate further.",
    },
  ],
  INVALIDATION: [
    {
      label: "A",
      tag: "The Hard Grey Rock",
      script: "Okay.",
      why: "A single-word acknowledgement without agreement starves the invalidator of the emotional engagement they need to continue the pattern.",
    },
    {
      label: "B",
      tag: "The Boundary Setter",
      script: "My feelings are valid regardless of whether you understand them.",
      why: "This re-establishes your emotional autonomy. It removes the implicit premise that your feelings require their approval to exist.",
    },
    {
      label: "C",
      tag: "The Exit",
      script: "I don't think this conversation is productive right now. Let's pause.",
      why: "Exiting a loop of invalidation prevents the cumulative erosion effect. Each additional exchange deepens the damage — breaking the cycle is the priority.",
    },
  ],
  DEFAULT: [
    {
      label: "A",
      tag: "The Hard Grey Rock",
      script: "I understand.",
      why: "This neutral acknowledgement gives no emotional foothold. The manipulator cannot escalate without a reaction to feed on.",
    },
    {
      label: "B",
      tag: "The Boundary Setter",
      script: "I'm not comfortable with how this conversation is going. I need to set a boundary here.",
      why: "Naming the discomfort without blame shifts the dynamic. It forces the other party to acknowledge the boundary or expose their intent.",
    },
    {
      label: "C",
      tag: "The Exit",
      script: "I'm going to take some space from this conversation.",
      why: "Strategic withdrawal is not avoidance — it's a calculated move to prevent further manipulation and protect your emotional state.",
    },
  ],
};

function getScripts(verdict: string): ResponseScript[] {
  return RESPONSE_SCRIPTS[verdict] || RESPONSE_SCRIPTS.DEFAULT;
}

const NeutralizerLabDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reportRef = useRef<HTMLDivElement>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const state = location.state as {
    message?: string;
    score?: number;
    verdict?: string;
  } | null;

  const message = state?.message || "No message provided.";
  const score = state?.score || 7.5;
  const verdict = state?.verdict || "MANIPULATION DETECTED";
  const scripts = getScripts(verdict);

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
    toast({ title: "Response copied to clipboard" });
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#121212",
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "GreyRockAI-Forensic-Report.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: "Forensic report downloaded" });
    } catch {
      toast({ title: "Export failed", variant: "destructive" });
    } finally {
      setTimeout(() => setExporting(false), 1000);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      style={{ background: "linear-gradient(180deg, hsl(0 0% 8%) 0%, hsl(0 0% 5%) 100%)" }}
    >
      {/* Top bar with logo home-link */}
      <div className="border-b border-border/40 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 group"
            >
              <Shield className="w-4 h-4 text-cyan" />
              <span className="text-sm font-mono text-muted-foreground group-hover:text-foreground tracking-wider transition-colors">
                GREYROCK AI — NEUTRALIZER LAB
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Back to Dashboard link */}
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>

      {/* Report body */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div ref={reportRef} className="space-y-8">
          {/* Forensic Report Header */}
          <section className="border border-border/50 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-border/30 flex items-center justify-between">
              <h1 className="text-lg font-mono font-bold text-foreground tracking-tight">
                FORENSIC REPORT
              </h1>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-success/10 border border-success/30">
                <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                <span className="text-xs font-mono text-success tracking-wider">
                  STRATEGY GENERATED — DE-ESCALATION ACTIVE
                </span>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Original message */}
              <div>
                <span className="text-xs font-mono text-muted-foreground tracking-wider block mb-2">
                  ORIGINAL MESSAGE
                </span>
                <div className="p-4 rounded-md bg-muted/30 border border-border/30">
                  <p className="text-sm text-foreground/90 leading-relaxed font-mono">
                    "{message}"
                  </p>
                </div>
              </div>

              {/* Score + Verdict */}
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-xs font-mono text-muted-foreground tracking-wider block mb-1">
                    THREAT SCORE
                  </span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-mono font-bold text-foreground">{score}</span>
                    <span className="text-sm text-muted-foreground">/10</span>
                  </div>
                </div>
                <div className="h-10 w-px bg-border/40" />
                <div>
                  <span className="text-xs font-mono text-muted-foreground tracking-wider block mb-1">
                    PRIMARY PATTERN
                  </span>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-drama-high" />
                    <span className="text-lg font-mono font-bold text-foreground tracking-wide">
                      {verdict}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Response Scripts */}
          <section className="border border-border/50 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-border/30">
              <h2 className="text-base font-mono font-bold text-foreground tracking-wider">
                RECOMMENDED RESPONSES
              </h2>
              <p className="text-xs text-muted-foreground mt-1 font-mono">
                Select and deploy the neutralization strategy best suited to your context.
              </p>
            </div>

            <div className="divide-y divide-border/20">
              {scripts.map((s, i) => (
                <div key={i} className="px-6 py-5 space-y-3">
                  {/* Script header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-md bg-cyan/10 border border-cyan/20 flex items-center justify-center text-xs font-mono font-bold text-cyan">
                        {s.label}
                      </span>
                      <span className="text-sm font-mono font-semibold text-foreground tracking-wide">
                        {s.tag}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(s.script, i)}
                      className="text-muted-foreground hover:text-cyan h-8 px-2 gap-1.5"
                    >
                      {copiedIdx === i ? (
                        <Check className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      <span className="text-xs font-mono">
                        {copiedIdx === i ? "COPIED" : "COPY"}
                      </span>
                    </Button>
                  </div>

                  {/* The script */}
                  <div className="p-4 rounded-md bg-muted/20 border border-border/30">
                    <p className="text-sm text-foreground leading-relaxed">"{s.script}"</p>
                  </div>

                  {/* Why it works */}
                  <div className="flex items-start gap-2 pl-1">
                    <span className="text-xs font-mono text-cyan/70 mt-0.5 shrink-0">
                      WHY THIS WORKS:
                    </span>
                    <p className="text-xs text-muted-foreground leading-relaxed">{s.why}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Disclaimer */}
          <div className="px-4 py-3 rounded-md border border-border/20 bg-muted/10">
            <p className="text-[11px] text-muted-foreground/60 font-mono leading-relaxed text-center">
              DISCLAIMER — This analysis is generated by AI and is not a substitute for
              professional advice. If you are in danger, contact local authorities immediately.
            </p>
          </div>
        </div>

        {/* Export button */}
        <div className="mt-8 flex justify-center">
          <Button
            onClick={handleExportPDF}
            disabled={exporting}
            className="h-12 px-8 rounded-xl font-mono font-bold tracking-wider bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground hover:opacity-90 transition-opacity gap-2"
          >
            <FileText className="w-4 h-4" />
            {exporting ? "EXPORTING..." : "EXPORT FORENSIC REPORT"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NeutralizerLabDetail;
